<template>
  <div>
    <b-button class="p-2 fz-inherit" variant="outline-secondary" @click="isOpen = true">
      <div class="d-flex align-items-center">
        <mdicon name="CogOutline" class="color-black mr-2" size="36" />
        <span class="color-black">Configure</span>
      </div>
    </b-button>

    <b-modal :id="'fallback-carriers-modal-' + company.id" v-model="isOpen" no-footer centered>
      <template #title> Fallback settings · {{ company.name }} </template>
      <div class="mb-3">
        <h6>Available fallback carriers</h6>
        <p class="small text-muted">
          Choose which carriers may be used as fallback. Each carrier's own weight limits and delivery rules still
          apply.
        </p>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <span>DHL Freight</span>
          <CustomerBooleanSetting :company="company" field="isDhlFallbackEnabled" label="DHL Freight fallback" />
        </div>
        <div class="d-flex justify-content-between align-items-center">
          <span>PostNord</span>
          <CustomerBooleanSetting :company="company" field="isPostnordFallbackEnabled" label="PostNord fallback" />
        </div>
      </div>
      <p v-if="enabledCarriers.length" class="small" role="status">
        Available above the customer threshold: <strong>{{ enabledCarriers.join(' and ') }}</strong>. One eligible service is selected for the shipment.
      </p>
      <p v-else class="small text-danger" role="status">
        No fallback carriers enabled. Shipments above a saved threshold cannot be routed.
      </p>
      <FallbackWeightLimit
        :model-value="weightLimit"
        :company-name="company.name"
        :input-id="'fallback-weight-' + company.id"
        :saving="savingWeight"
        :error="weightError"
        @save="saveWeightLimit"
      />
    </b-modal>
  </div>
</template>

<script lang="ts">
import type { Company } from '@/types/Models/Company'
import { BButton, BModal } from 'bootstrap-vue-next'
import { defineComponent, ref, computed, type ComputedRef, type Ref, type PropType } from 'vue'
import { useCustomersStore } from '@/store/customers'
import CompanyProxy from '@/proxies/CompanyProxy'
import FallbackWeightLimit from '@/views/Components/Customer/FallbackWeightLimit.vue'
import CustomerBooleanSetting from '@/views/Components/Customer/CustomerBooleanSetting.vue'

export default defineComponent({
  name: 'FallbackCarriersModal',
  components: { BButton, BModal, FallbackWeightLimit, CustomerBooleanSetting },
  props: {
    company: {
      type: Object as PropType<Company>,
      required: true,
    },
  },
  setup(props) {
    const isOpen: Ref<boolean> = ref(false)
    const customersStore = useCustomersStore()
    const weightLimit: Ref<number | null> = ref(props.company.packageFallbackWeightLimit ?? null)
    const savingWeight: Ref<boolean> = ref(false)
    const weightError: Ref<string> = ref('')
    const saveWeightLimit = (value: number | null): void => {
      savingWeight.value = true
      weightError.value = ''
      const data = { packageFallbackWeightLimit: value }
      new CompanyProxy()
        .update(props.company.id, data)
        .then(() => {
          const customer = customersStore.listCustomers?.find((item) => item.id === props.company.id)
          if (customer) customer.packageFallbackWeightLimit = value
          weightLimit.value = value
        })
        .catch(() => {
          weightError.value = 'Could not save the weight limit. Your changes are still here. Try again.'
        })
        .finally(() => {
          savingWeight.value = false
        })
    }
    const enabledCarriers: ComputedRef<string[]> = computed(() => [
      ...(props.company.isDhlFallbackEnabled ? ['DHL Freight'] : []),
      ...(props.company.isPostnordFallbackEnabled ? ['PostNord'] : []),
    ])

    return {
      isOpen,
      weightLimit,
      savingWeight,
      weightError,
      saveWeightLimit,
      enabledCarriers,
    }
  },
})
</script>
